import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

import { 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

import { 
  FaUsers, FaBox, FaTags, FaGift, FaShoppingCart, FaCheckCircle, 
  FaTruck, FaTimesCircle, FaChartLine, FaRupeeSign, FaCalendarAlt, FaClock, FaCog 
} from 'react-icons/fa';

import './adminQuickLinks.css';
import AdminPageLoader from './AdminPageLoader';

function AdminHomePage() {
  const [counts, setCounts] = useState({
    usersCount: 0,
    categoriesCount: 0,
    productsCount: 0,
    brandsCount: 0,
    offersCount: 0,
    ordersCount: 0,
    deliveredOrdersCount: 0,
    pendingOrdersCount: 0,
    confirmedOrdersCount: 0,
    shippedOrdersCount: 0,
    cancelledOrdersCount: 0
  });

  const [salesData, setSalesData] = useState({
    dailySales: [],
    monthlySales: [],
    salesSummary: {},
    topProducts: [],
    orderStatusDistribution: []
  });

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('daily'); // default is daily
  const [recentCustomers, setRecentCustomers] = useState([]);

  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error("Unauthorized access! Redirecting...");
        setTimeout(() => navigate('/admin-login'), 2000);
        return;
      }
      try {
        setLoading(true);
        const baseURL = API_URL || 'http://localhost:3500';

        // Single optimised endpoint — replaces 7 separate calls
        const { data } = await axios.get(`${baseURL}/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setCounts(data.counts || {});
        setSalesData({
          dailySales:              data.dailySales              || [],
          monthlySales:            data.monthlySales            || [],
          salesSummary:            data.salesSummary            || {},
          topProducts:             data.topProducts             || [],
          orderStatusDistribution: data.orderStatusDistribution || []
        });
        setRecentCustomers(data.recentCustomers || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        toast.error("Failed to fetch dashboard data. Try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    // Use theme colors and other distinct colors
    const colors = {
      pending: '#1a237e',      // dark blue (theme)
      accepted: '#ffff01',     // yellow (theme)
      shipped: '#17A2B8',      // teal
      delivered: '#28A745',    // green
      cancelled: '#DC3545',    // red
      processing: '#FFA500',   // orange
      returned: '#8e24aa',     // purple
      failed: '#ff7043',       // orange-red
    };
    return colors[status] || '#6C757D';
  };

  if (loading) {
    return (
      <div className="container-fluid p-3 pt-2 g-0 adma">
        <div className="row">
          <div className="col-12">
            <div className="section-title">
              <h4 className='mt-1'>BEST BASKET - ADMIN DASHBOARD</h4>
              <p className="text-muted p-0 m-0">Welcome back! Here's what's happening with your business today.</p>
            </div>
          </div>
        </div>
        <AdminPageLoader message="Loading dashboard data..." />
      </div>
    );
  }

  return (
    <div className="container-fluid p-3 pt-2 g-0 adma">
      <div className="row ">
        <div className="col-12">
          <div className="section-title">
            <h4 className='mt-1'>BEST BASKET - ADMIN DASHBOARD</h4>
            <p className="text-muted p-0 m-0">Welcome back! Here's what's happening with your business today.</p>
          </div>
        </div>
      </div>
      <div className="row dashboard-actions-row mb-4">
        <div>
          <Link to="/admin/shipped-orders" className="dashboard-action-card">
            <FaTruck />
            <span>Shipped Orders</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/confirmed-orders" className="dashboard-action-card">
            <FaCheckCircle />
            <span>Confirmed Orders</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/all-orders" className="dashboard-action-card">
            <FaShoppingCart />
            <span>All Orders</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/products" className="dashboard-action-card">
            <FaBox />
            <span>Products</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/categories" className="dashboard-action-card">
            <FaTags />
            <span>Categories</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/users" className="dashboard-action-card">
            <FaUsers />
            <span>Customers</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/brands" className="dashboard-action-card">
            <FaTags />
            <span>Brands</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/offers" className="dashboard-action-card">
            <FaGift />
            <span>Offers</span>
          </Link>
        </div>
        <div>
          <Link to="/admin/admin-report" className="dashboard-action-card">
            <FaChartLine />
            <span>Reports</span>
          </Link>
        </div>
      </div>
      <div className="row dashboard-stats-row mb-4">
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card dashboard-stats-card">
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <span className="stats-icon-wrapper" style={{ background: 'rgba(46, 125, 50, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaRupeeSign style={{ fontSize: 20, color: '#2e7d32' }} />
              </span>
              <div>
                <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Today's Sales</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#2e7d32', lineHeight: 1.2 }}>{formatCurrency(salesData.salesSummary.todaySales || 0)}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesData.salesSummary.todayOrders || 0} orders</div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card dashboard-stats-card">
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <span className="stats-icon-wrapper" style={{ background: 'rgba(21, 101, 192, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaCalendarAlt style={{ fontSize: 20, color: '#1565c0' }} />
              </span>
              <div>
                <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This Week</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1565c0', lineHeight: 1.2 }}>{formatCurrency(salesData.salesSummary.weekSales || 0)}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesData.salesSummary.weekOrders || 0} orders</div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card dashboard-stats-card">
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <span className="stats-icon-wrapper" style={{ background: 'rgba(0, 131, 143, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaChartLine style={{ fontSize: 20, color: '#00838f' }} />
              </span>
              <div>
                <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>This Month</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#00838f', lineHeight: 1.2 }}>{formatCurrency(salesData.salesSummary.monthSales || 0)}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesData.salesSummary.monthOrders || 0} orders</div>
            </div>
          </div>
        </div>
        <div className="col-lg-3 col-md-6 col-sm-12">
          <div className="card dashboard-stats-card">
            <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
              <span className="stats-icon-wrapper" style={{ background: 'rgba(239, 108, 0, 0.08)', borderRadius: 8, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FaRupeeSign style={{ fontSize: 20, color: '#ef6c00' }} />
              </span>
              <div>
                <div style={{ fontSize: 12, color: '#4b5563', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Sales</div>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#ef6c00', lineHeight: 1.2 }}>{formatCurrency(salesData.salesSummary.totalSales || 0)}</div>
              <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 500, marginTop: 4 }}>{salesData.salesSummary.totalOrders || 0} orders</div>
            </div>
          </div>
        </div>
      </div>
      <div className="row dashboard-charts-row">
        {(() => {
          const totalOrdersCount = salesData.orderStatusDistribution
            ? salesData.orderStatusDistribution
                .filter(entry => entry.status !== 'pending')
                .reduce((acc, entry) => acc + entry.count, 0)
            : 0;
          return (
            <React.Fragment>
              <div className="dashboard-charts-left-col">
                <div className="card dashboard-chart-card">
                  <div className="card-header">
                    <h5 className="mb-0">Sales Analytics</h5>
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm sales-tab-btn${activeTab === 'daily' ? ' active daily-tab' : ''}`}
                        onClick={() => setActiveTab('daily')}
                      >
                        Daily
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm sales-tab-btn${activeTab === 'monthly' ? ' active monthly-tab' : ''}`}
                        onClick={() => setActiveTab('monthly')}
                      >
                        Monthly
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <ResponsiveContainer width="100%" height={220}>
                      {activeTab === 'daily' ? (
                        <AreaChart data={salesData.dailySales}>
                          <defs>
                            <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#1a237e" stopOpacity={0.25} />
                              <stop offset="95%" stopColor="#1a237e" stopOpacity={0.0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                          <XAxis 
                            dataKey="date" 
                            stroke="#9ca3af" 
                            tickLine={false} 
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }} 
                            dy={8}
                          />
                          <YAxis 
                            stroke="#9ca3af" 
                            tickLine={false} 
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }} 
                            dx={-8}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#ffffff', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '6px', 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              fontSize: '12px',
                              padding: '8px 12px'
                            }}
                            itemStyle={{ color: '#1a237e', fontWeight: 600 }}
                            labelStyle={{ color: '#6b7280', fontWeight: 500, fontSize: '11px', marginBottom: 2 }}
                            formatter={(value) => [formatCurrency(value), 'Sales']}
                            labelFormatter={(label) => `Date: ${label}`}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="sales" 
                            stroke="#1a237e" 
                            strokeWidth={3}
                            fill="url(#salesGrad)" 
                            activeDot={{ r: 6, stroke: '#1a237e', strokeWidth: 2, fill: '#ffff01' }}
                          />
                        </AreaChart>
                      ) : (
                        <BarChart data={salesData.monthlySales}>
                          <defs>
                            <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#1a237e" stopOpacity={1} />
                              <stop offset="100%" stopColor="#283593" stopOpacity={0.7} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" vertical={false} />
                          <XAxis 
                            dataKey="month" 
                            stroke="#9ca3af" 
                            tickLine={false} 
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }} 
                            dy={8}
                          />
                          <YAxis 
                            stroke="#9ca3af" 
                            tickLine={false} 
                            axisLine={false}
                            tick={{ fontSize: 10, fill: '#4b5563', fontWeight: 500 }} 
                            dx={-8}
                          />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#ffffff', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '6px', 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              fontSize: '12px',
                              padding: '8px 12px'
                            }}
                            itemStyle={{ color: '#1a237e', fontWeight: 600 }}
                            labelStyle={{ color: '#6b7280', fontWeight: 500, fontSize: '11px', marginBottom: 2 }}
                            formatter={(value) => [formatCurrency(value), 'Sales']}
                          />
                          <Bar 
                            dataKey="sales" 
                            fill="url(#barGrad)" 
                            radius={[4, 4, 0, 0]}
                            maxBarSize={45}
                          />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="dashboard-charts-right-col">
                <div className="card dashboard-chart-card">
                  <div className="card-header">
                    <h5 className="mb-0">Order Status Distribution</h5>
                  </div>
                  <div className="card-body">
                    <div style={{ position: 'relative', width: '100%', height: 200 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={salesData.orderStatusDistribution.filter(entry => entry.status !== 'pending')}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={false}
                            innerRadius={60}
                            outerRadius={80}
                            paddingAngle={4}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {salesData.orderStatusDistribution.filter(entry => entry.status !== 'pending').map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={getStatusColor(entry.status)} 
                                stroke="#ffffff"
                                strokeWidth={2}
                              />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: '#ffffff', 
                              border: '1px solid #e5e7eb', 
                              borderRadius: '6px', 
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                              fontSize: '12px',
                              padding: '6px 10px'
                            }}
                            itemStyle={{ color: '#1a237e', fontWeight: 600 }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        pointerEvents: 'none'
                      }}>
                        <div style={{ fontSize: 26, fontWeight: 700, color: '#1a237e', lineHeight: 1.1 }}>{totalOrdersCount}</div>
                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginTop: 1, letterSpacing: '0.5px' }}>Orders</div>
                      </div>
                    </div>
                    {/* Custom legend below the chart */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginTop: 12 }}>
                      {salesData.orderStatusDistribution.filter(entry => entry.status !== 'pending').map((entry, index) => (
                        <div key={entry.status} style={{ display: 'flex', alignItems: 'center', margin: '4px 10px' }}>
                          <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            backgroundColor: getStatusColor(entry.status),
                            borderRadius: '50%',
                            marginRight: 6,
                            border: '1.5px solid #eee'
                          }} />
                          <span style={{ fontSize: 12, fontWeight: 500, color: '#4b5563', textTransform: 'capitalize' }}>{entry.status} ({entry.count})</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })()}
      </div>
      <div className="row dashboard-top-products-row">
        <div className="col-12 p-0">
          <div className="card dashboard-top-products-card">
            <div className="card-header">
              <h5 className="mb-0">Top Selling Products</h5>
            </div>
            <div className="card-body">
              {salesData.topProducts.length > 0 ? (
                <div className="row m-0 w-100">
                  <div className="col-lg-6 col-md-12 p-0 border-right-desktop">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0" style={{ minWidth: 320 }}>
                        <thead>
                          <tr>
                            <th style={{ color: '#1a237e', fontWeight: 700 }}>Product</th>
                            <th style={{ color: '#1a237e', fontWeight: 700 }}>Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesData.topProducts.slice(0, 5).map((product, index) => (
                            <tr key={index}>
                              <td>
                                <span className="product-rank-badge">{index + 1}</span>
                                {product.name}
                              </td>
                              <td>
                                <span className="product-count-badge">{product.quantity}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="col-lg-6 col-md-12 p-0">
                    <div className="table-responsive">
                      <table className="table table-hover mb-0" style={{ minWidth: 320 }}>
                        <thead>
                          <tr>
                            <th style={{ color: '#1a237e', fontWeight: 700 }}>Product</th>
                            <th style={{ color: '#1a237e', fontWeight: 700 }}>Count</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesData.topProducts.slice(5, 10).map((product, index) => (
                            <tr key={index + 5}>
                              <td>
                                <span className="product-rank-badge">{index + 6}</span>
                                {product.name}
                              </td>
                              <td>
                                <span className="product-count-badge">{product.quantity}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-muted text-center mb-0">No sales data available</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row dashboard-new-customers-row">
        <div className="col-12 p-0">
          <div className="card dashboard-new-customers-card">
            <div className="card-header">
              <h5 className="mb-0">New Customers</h5>
            </div>
            <div className="card-body">
              {recentCustomers.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ minWidth: 320 }}>
                    <thead>
                      <tr>
                        <th style={{ color: '#1a237e', fontWeight: 700 }}>#</th>
                        <th style={{ color: '#1a237e', fontWeight: 700 }}>Name</th>
                        <th style={{ color: '#1a237e', fontWeight: 700 }}>Mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentCustomers.map((user, idx) => (
                        <tr key={user._id}>
                          <td>{idx + 1}</td>
                          <td>{user.address && user.address[0]?.name ? user.address[0].name : 'N/A'}</td>
                          <td>{user.mobile}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted text-center mb-0">No recent customers found.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminHomePage; 